/*
 * DISPLAY SIMULATION of ranking version r2 (decided 2026-09-07) — never
 * authoritative. The authoritative implementation is graphy
 * app/services/first_responder/ranking_policy.rb (:reliability_first).
 *
 * RANKING ORDER (r2, decided 2026-09-07):
 *   hard gates -> Dependability tier -> distance bucket -> favorite ->
 *   prior-client -> everyone else -> higher dependability score (missing
 *   last) -> closer exact distance (missing last) -> stable worker-ID
 *   tie-breaker
 *
 * The dependability score and the exact distance are tie-breaks ONLY: they
 * decide only between workers who already tie on tier, distance bucket AND
 * relationship. The score never outranks a tier or a band — a lower-tier or
 * farther-band worker never beats a higher-tier or closer-band worker no
 * matter how their scores compare.
 *
 * Deliberate exclusions from the sort key:
 *   - shiftsWithClient COUNT: prior-client is a boolean step; the count is
 *     relationship evidence, never a ranking rule.
 *   - Preferred Pool: the tables exist in Graphy code/schema, but First
 *     Responder Runner currently supplies preferred_pool: false. It is
 *     therefore NOT an input here at all — no unreachable branch is retained.
 *     It may appear in copy only as a disabled future capability. Nothing here
 *     may claim production flag state, pool counts or membership counts.
 *
 * Workers with no usable Dependability evidence (tier null) are VISIBLE but
 * NEVER selectable while activation slots are zero: they sort below every
 * tiered worker and cannot enter the selection. An all-unknown pool yields
 * `no_selectable_candidates` — a skip, not a successful empty selection.
 */

/*
 * The complete ranking order, as displayed to an operator. Every step is
 * read-only: the sequence below is what `sortKey` executes, and changing it is
 * a reviewed ranking-policy version plus a code change — never an Admin
 * setting. This constant is the SINGLE source for the rendered list, so the
 * displayed order cannot drift from the executed one.
 *
 * `unavailable` marks a capability that is shown but is not an input at all.
 */
export type LockedRankingStep = {
  key: string;
  label: string;
  note?: string;
  unavailable?: boolean;
};

export const LOCKED_RANKING_ORDER: readonly LockedRankingStep[] = [
  {
    key: 'gates',
    label: 'Hard eligibility & safety gates',
    note: 'locked',
  },
  {
    key: 'tier',
    label: 'Dependability tier',
    note: 'locked first for this pilot',
  },
  { key: 'distance_bucket', label: 'Distance bucket' },
  { key: 'favorite', label: 'Favorite for this client' },
  { key: 'prior_client', label: 'Previously worked for this client' },
  { key: 'new_to_client', label: 'Everyone else', note: 'new to this client' },
  {
    key: 'preferred_pool',
    label: 'Preferred Pool',
    note: 'unavailable in this First Responder pilot',
    unavailable: true,
  },
  {
    key: 'score',
    label: 'Higher dependability score',
    note: 'inside the same tier, band and relationship',
  },
  {
    key: 'distance_exact',
    label: 'Closer distance',
    note: 'exact miles, only when the score ties',
  },
  {
    key: 'stable_tie_breaker',
    label: 'Stable technical tie-breaker',
    note: 'system-managed',
  },
];

export const LOCKED_RANKING_NOTE =
  'Locked for this pilot as ranking version r2 (decided 2026-09-07). Changing the order requires a separately reviewed ranking-policy version. The dependability score only breaks ties inside a tier and a distance band — it never outranks a tier or a band.';

export type PreviewTier = 'platinum' | 'gold' | 'standard' | 'monitored';

export type RankableWorker = {
  id: string;
  tier: PreviewTier | null; // null = Unknown dependability
  score: number | null; // tie-break only, after tier/bucket/relationship; missing sorts last
  distanceMiles: number | null;
  favorite: boolean;
  shiftsWithClient: number | null;
};

export const DISTANCE_BUCKETS = [10, 20, 30, 50];

const TIER_RANK: Record<PreviewTier, number> = {
  platinum: 0,
  gold: 1,
  standard: 2,
  monitored: 3,
};

// Unknown tier -> 9, matching the backend's TIER_RANK.fetch(tier, 9): below
// every known tier.
export const tierRank = (w: Pick<RankableWorker, 'tier'>): number =>
  w.tier === null ? 9 : TIER_RANK[w.tier];

// Coarse buckets on purpose (backend comment: finer buckets would imply a
// precision the data does not support). Unknown distance sorts after the
// widest bucket.
export const distanceBucket = (
  w: Pick<RankableWorker, 'distanceMiles'>,
): number => {
  const d = w.distanceMiles;
  if (d === null) return DISTANCE_BUCKETS.length + 1;
  const idx = DISTANCE_BUCKETS.findIndex((b) => d <= b);
  return idx === -1 ? DISTANCE_BUCKETS.length : idx;
};

// 0 = favorite, 1 = has worked for this client, 2 = new. Boolean steps only.
export const relationshipRank = (
  w: Pick<RankableWorker, 'favorite' | 'shiftsWithClient'>,
): number => {
  if (w.favorite) return 0;
  if ((w.shiftsWithClient ?? 0) > 0) return 1;
  return 2;
};

// Higher score first; a missing score sorts last within the tie (r2, decided
// 2026-09-07). Only reached once tier, distance bucket and relationship have
// already tied — never outranks a tier or a band.
const scoreKey = (w: Pick<RankableWorker, 'score'>): number =>
  w.score === null ? Infinity : -w.score;

// Closer exact distance first; a missing distance sorts last within the tie.
// Only reached once the score has also tied.
const exactDistanceKey = (w: Pick<RankableWorker, 'distanceMiles'>): number =>
  w.distanceMiles === null ? Infinity : w.distanceMiles;

const sortKey = (
  w: RankableWorker,
): [number, number, number, number, number, string] => [
  tierRank(w),
  distanceBucket(w),
  relationshipRank(w),
  scoreKey(w),
  exactDistanceKey(w),
  w.id,
];

const compare = (a: RankableWorker, b: RankableWorker): number => {
  const ka = sortKey(a);
  const kb = sortKey(b);
  for (let i = 0; i < ka.length; i += 1) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
};

export type RankResult = {
  // Full policy order, Unknowns last. This IS the display order.
  ordered: RankableWorker[];
  // Top-N selectable workers (tier !== null). May be shorter than limit.
  selected: RankableWorker[];
  result: 'selected' | 'no_selectable_candidates';
};

export const rankWorkers = (
  workers: RankableWorker[],
  { limit }: { limit: number },
): RankResult => {
  const ordered = [...workers].sort(compare);
  const selected = ordered.filter((w) => w.tier !== null).slice(0, limit);
  return {
    ordered,
    selected,
    result: selected.length === 0 ? 'no_selectable_candidates' : 'selected',
  };
};

export const bucketLabel = (
  w: Pick<RankableWorker, 'distanceMiles'>,
): string => {
  const b = distanceBucket(w);
  if (b >= DISTANCE_BUCKETS.length) {
    return w.distanceMiles === null
      ? 'distance unknown'
      : `beyond ${DISTANCE_BUCKETS[DISTANCE_BUCKETS.length - 1]} mi`;
  }
  const lower = b === 0 ? 0 : DISTANCE_BUCKETS[b - 1];
  return `${lower}–${DISTANCE_BUCKETS[b]} mi`;
};

const relationshipLabel = (
  w: Pick<RankableWorker, 'favorite' | 'shiftsWithClient'>,
): string => {
  if (w.favorite) return 'favorite of this client';
  if ((w.shiftsWithClient ?? 0) > 0) return 'has worked for this client';
  return 'new to this client';
};

// Plain-English evidence for Why selected / Why not selected — written for an
// operator, not an engineer (the ordering rules themselves are explained once
// in the "How this list is ordered" legend, not re-litigated per row). Never
// implies a score-vs-score comparison the policy did not make.
export const explainWorker = (
  w: RankableWorker,
  position: number,
  selectedCount: number,
  limit: number,
  total: number,
): string => {
  if (w.tier === null) {
    return 'No usable Dependability history — shown for transparency, never auto-selected';
  }
  const tierWord = w.tier.charAt(0).toUpperCase() + w.tier.slice(1);
  const basis = `${tierWord} tier, ${bucketLabel(w)}, ${relationshipLabel(w)}`;
  if (position <= selectedCount) {
    return `Selected #${position}: ${basis}`;
  }
  return `#${position} of ${total} — below the top ${limit} (${basis})`;
};
